import React, { useEffect, useState } from 'react'
import Select from 'react-select'
import DataTable from 'react-data-table-component'
import { getDataWithStudent, deleteIdData } from '../../lib/courselesson'
import { useRoleBasedUrl } from '../../Hooks/useRoleBasedUrl'
import { useLayout } from '../../Hooks/useLayout'
import { getLessonColumns } from '../../Data/Lessons/LessonListData'
import LessonModal from './LessonModal'
import { ToastNotification } from './CourseReport/CourseReportComponents'
import { ConfirmModal } from '../Common/ConfirmModal'
import { getStudentIdData as getStudentsGrades } from '../../lib/gradeinmonths'

const LessonList = ({ studentId, isStudent = true, isProfile = false }) => {
	const [lessons, setLessons] = useState([])
	const [filterText, setFilterText] = useState('')
	const [loading, setLoading] = useState(true)
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [selectedLesson, setSelectedLesson] = useState(null)
	const [toastMessage, setToastMessage] = useState('')
	const [showToast, setShowToast] = useState(false)
	const [showConfirmModal, setShowConfirmModal] = useState(false)
	const [deleteId, setDeleteId] = useState(null)
	const [departmentFilter, setDepartmentFilter] = useState([])
	const [courseFilter, setCourseFilter] = useState([])
	const [levelFilter, setLevelFilter] = useState([])
	const [grades, setGrades] = useState([])
	const role = useRoleBasedUrl()
	const layout = useLayout()

	const filteredLessons = lessons.filter((item) => {
		const matchesSearch =
			!filterText ||
			item.coursename?.toLowerCase().includes(filterText.toLowerCase()) ||
			item.department?.toLowerCase().includes(filterText.toLowerCase()) ||
			item.level?.toLowerCase().includes(filterText.toLowerCase()) ||
			item.courseyear?.toString().toLowerCase().includes(filterText.toLowerCase())

		const matchesDepartment = departmentFilter.length === 0 || departmentFilter.some((f) => f.value === item.department)
		const matchesCourse = courseFilter.length === 0 || courseFilter.some((f) => f.value === item.coursename)
		const matchesLevel = levelFilter.length === 0 || levelFilter.some((f) => f.value === item.level)

		return matchesSearch && matchesDepartment && matchesCourse && matchesLevel
	})

	const departmentOptions = [...new Set(lessons.map((l) => l.department).filter(Boolean))].sort().map((dept) => ({ value: dept, label: dept }))
	const courseOptions = [...new Set(lessons.map((l) => l.coursename).filter(Boolean))].sort().map((course) => ({ value: course, label: course }))
	const levelOptions = [...new Set(lessons.map((l) => l.level).filter(Boolean))].sort().map((lvl) => ({ value: lvl, label: lvl }))

	const showToastMessage = (message) => {
		setToastMessage(message)
		setShowToast(true)
		setTimeout(() => setShowToast(false), 3000)
	}

	const handleDelete = (id) => {
		setDeleteId(id)
		setShowConfirmModal(true)
	}

	const confirmDelete = async () => {
		if (deleteId) {
			try {
				await deleteIdData(deleteId)
				showToastMessage('Course deleted successfully!')
				await getData()
			} catch (error) {
				console.error('Error deleting lesson:', error)
			} finally {
				setDeleteId(null)
			}
		}
	}

	const handleEdit = (lesson) => {
		setSelectedLesson(lesson)
		setIsModalOpen(true)
	}

	const handleAddTask = () => {
		setSelectedLesson(null)
		setIsModalOpen(true)
	}

	const toggleModal = () => {
		setIsModalOpen(!isModalOpen)
	}

	const getData = async (message) => {
		try {
			setLoading(true)
			const lessonData = await getDataWithStudent(studentId)
			setLessons(lessonData)
			
			const gradesData = await getStudentsGrades(studentId)
			setGrades(gradesData)

			if (message && typeof message === 'string') {
				showToastMessage(message)
			}
		} catch (error) {
			console.error('Error fetching lessons:', error)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		if (studentId) {
			getData()
		}
	}, [studentId])

	const columns = getLessonColumns(studentId, isStudent, role, layout, handleDelete, handleEdit, isProfile, grades)

	const customStyles = {
		headCells: {
			style: {
				fontSize: '11px',
				fontWeight: 'bold',
				textTransform: 'uppercase',
				color: '#000'
			}
		},
		rows: {
			style: {
				minHeight: '60px',
				fontSize: '15px'
			}
		},
		cells: {
			style: {
				paddingTop: '16px',
				paddingBottom: '16px',
				fontSize: '14px'
			}
		}
	}

	return (
		<div className="col-12 p-0">
			<ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
			<div className="d-flex p-2 pb-0 justify-content-between align-items-center flex-wrap gap-3">
				<h4 className="px-2 mb-0">{!isProfile && 'Courses'}</h4>
				{!isProfile && (
					<div className="d-flex gap-2 flex-wrap align-items-center">
						<div className="d-flex gap-2 align-items-center" style={{ minWidth: '450px' }}>
							<Select
								isMulti
								name="departments"
								options={departmentOptions}
								className="basic-multi-select"
								classNamePrefix="select"
								placeholder="Departments"
								value={departmentFilter}
								onChange={setDepartmentFilter}
								styles={{
									container: (base) => ({ ...base, minWidth: '150px' }),
									control: (base) => ({ ...base, minHeight: '31px', fontSize: '13px' }),
									valueContainer: (base) => ({ ...base, padding: '0 6px' }),
									input: (base) => ({ ...base, margin: '0' }),
									indicatorsContainer: (base) => ({ ...base, height: '31px' })
								}}
							/>
							<Select
								isMulti
								name="courses"
								options={courseOptions}
								className="basic-multi-select"
								classNamePrefix="select"
								placeholder="Courses"
								value={courseFilter}
								onChange={setCourseFilter}
								styles={{
									container: (base) => ({ ...base, minWidth: '150px' }),
									control: (base) => ({ ...base, minHeight: '31px', fontSize: '13px' }),
									valueContainer: (base) => ({ ...base, padding: '0 6px' }),
									input: (base) => ({ ...base, margin: '0' }),
									indicatorsContainer: (base) => ({ ...base, height: '31px' })
								}}
							/>
							<Select
								isMulti
								name="levels"
								options={levelOptions}
								className="basic-multi-select"
								classNamePrefix="select"
								placeholder="Levels"
								value={levelFilter}
								onChange={setLevelFilter}
								styles={{
									container: (base) => ({ ...base, minWidth: '150px' }),
									control: (base) => ({ ...base, minHeight: '31px', fontSize: '13px' }),
									valueContainer: (base) => ({ ...base, padding: '0 6px' }),
									input: (base) => ({ ...base, margin: '0' }),
									indicatorsContainer: (base) => ({ ...base, height: '31px' })
								}}
							/>
						</div>
						<input
							type="text"
							placeholder="Search..."
							className="form-control form-control-sm"
							value={filterText}
							onChange={(e) => setFilterText(e.target.value)}
							style={{ width: '180px' }}
						/>
						<button
							onClick={handleAddTask}
							className="btn btn-primary btn-sm"
						>
							<i className="fa fa-plus me-1"></i>
							Add Course
						</button>
					</div>
				)}
			</div>

			{loading ? (
				<div className="text-center p-4">
					<div className="spinner-border" role="status">
						<span className="visually-hidden">Loading...</span>
					</div>
					<p className="mt-2">Loading lessons...</p>
				</div>
			) : (
				<div className="px-2">
					<DataTable
						columns={columns}
						data={filteredLessons}
						pagination
						paginationPerPage={50}
						paginationRowsPerPageOptions={[10, 25, 50, 100]}
						highlightOnHover
						striped
						responsive
						dense
						persistTableHead
						customStyles={customStyles}
						noDataComponent={
							<div className="text-center p-4">
								<p className="text-muted">No courses found</p>
							</div>
						}
					/>
				</div>
			)}
			<LessonModal
				isOpen={isModalOpen}
				toggle={toggleModal}
				studentId={studentId}
				lessonToEdit={selectedLesson}
				onSave={getData}
			/>
			<ConfirmModal
				isOpen={showConfirmModal}
				toggle={() => setShowConfirmModal(false)}
				onConfirm={confirmDelete}
				title="Delete Course"
				message="Are you sure you want to delete this course? This action cannot be undone."
			/>
		</div>
	)
}

export { LessonList }
